import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import client from '../client';
import { parseEnvelope } from '../envelope';

const progressSchema = z.object({
  processed_messages: z.number().int().nonnegative(),
  total_messages: z.number().int().nonnegative(),
});

const failureSchema = z.object({
  code: z.string(),
  retry_same_archive: z.boolean(),
});

export const inboundTakeoutSchema = z.object({
  import_id: z.string().uuid(),
  status: z.enum(['queued', 'importing', 'calibrating', 'complete', 'failed']),
  created_at: z.string(),
  completed_at: z.string().nullable(),
  progress: progressSchema.nullable(),
  counts: z.record(z.string(), z.number().int().nonnegative()),
  error: failureSchema.nullable(),
});

export type InboundTakeout = z.infer<typeof inboundTakeoutSchema>;

const queryKey = ['inbound-takeout'] as const;
export const MAX_TAKEOUT_BYTES = 90_000_000;

export function useInboundTakeout() {
  const queryClient = useQueryClient();
  const controller = useRef<AbortController | null>(null);
  const status = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await client.get('/api/inbound/takeout');
      return parseEnvelope(inboundTakeoutSchema.nullable(), response.data).data;
    },
    refetchInterval: (query) => {
      const value = query.state.data;
      return value && ['queued', 'importing', 'calibrating'].includes(value.status) ? 1500 : false;
    },
    retry: false,
  });

  const upload = useMutation({
    mutationFn: async ({ file, onProgress }: { file: File; onProgress?: (percent: number) => void }) => {
      controller.current?.abort();
      const next = new AbortController();
      controller.current = next;
      const form = new FormData();
      form.append('archive', file, file.name);
      const response = await client.post('/api/inbound/takeout', form, {
        timeout: 0,
        signal: next.signal,
        onUploadProgress: (event) => {
          if (event.total) onProgress?.(Math.round((event.loaded / event.total) * 100));
        },
      });
      return parseEnvelope(inboundTakeoutSchema, response.data).data;
    },
    retry: false,
    onSuccess: (value) => {
      queryClient.setQueryData(queryKey, value);
    },
  });

  useEffect(() => () => controller.current?.abort(), []);
  return { status, upload };
}
